import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import Footer from '../components/Footer.jsx';
import { askAhaMomentAI } from '../api/askAhaMomentAI.js';
import { getAuth } from 'firebase/auth';
import { trackEvent } from '../utils/analytics/trackEvent.js';
import { FaLightbulb, FaProjectDiagram, FaWater, FaUniversity } from 'react-icons/fa';
import Lottie from "lottie-react";
import tapAnimation from "../assets/animations/coin_sparkle.json"; // download a small "tap" animation

const PropFundingAILanding = () => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    trackEvent('aha_page_view', {
      screen: 'AhaMomentAILanding',
      channel: 'web',
      timestamp: Date.now(),
    });
  }, []);

  const lightColors = ["#FFE5D0", "#D6F0FF", "#DFFFE2", "#F0E5FF", "#F9F9F9"];

  const suggestionGroups = [
    {
      title: "📘 Basics",
      items: [
        { text: "Smart Money Concepts", icon: <FaLightbulb /> },
        { text: "Market Structure", icon: <FaProjectDiagram /> },
        { text: "Directional Bias", icon: <FaLightbulb /> },
        { text: "Chart Patterns", icon: <FaProjectDiagram /> },
      ]
    },
    {
      title: "💧 Liquidity",
      items: [
        { text: "Liquidity Grabs", icon: <FaWater /> },
        { text: "Liquidity Pools", icon: <FaWater /> },
        { text: "Liquidity Sweeps", icon: <FaWater /> },
        { text: "Stop Hunt", icon: <FaWater /> },
      ]
    },
    {
      title: "🏦 Order Blocks",
      items: [
        { text: "Order Block", icon: <FaUniversity /> },
        { text: "Breaker Blocks", icon: <FaUniversity /> },
        { text: "Mitigation Block", icon: <FaUniversity /> },
        { text: "Confirm Order Block", icon: <FaUniversity /> },
      ]
    },
    {
      title: "⚡ Advanced",
      items: [
        { text: "Fair Value Gap", icon: <FaLightbulb /> },
        { text: "Displacement", icon: <FaLightbulb /> },
        { text: "Change of Character", icon: <FaLightbulb /> },
        { text: "Institutional Order Flow", icon: <FaUniversity /> },
      ]
    }
  ];

  const handleAskAI = async (overrideInput) => {
    const usermessage = (overrideInput || input).trim();
    if (!usermessage) return;

    setLoading(true);

    const chatSessionId = uuidv4();
    const user = auth.currentUser;
    const userId = user?.uid || 'anonymous';

    trackEvent('aha_ask_ai_button_clicked', {
      screen: 'AhaMomentAILanding',
      user_id: userId,
      channel: 'web',
      message_length: usermessage.length,
      timestamp: Date.now(),
    });

    trackEvent('aha_ask_ai_click', {
      screen: 'AhaMomentAILanding',
      user_id: userId,
      chatsessionid: chatSessionId,
      message_length: usermessage.length,
      channel: 'web',
      timestamp: Date.now(),
    });

    navigate(`/learn/propfunding/space/${chatSessionId}`, {
      state: { question: usermessage },
    });

    askAhaMomentAI({
      userId,
      chatsessionid: chatSessionId,
      usermessage,
      messageCount: 0,
    })
      .then((airesponse) => {
        sessionStorage.setItem(`ahaResponse-${chatSessionId}`, JSON.stringify({
          question: usermessage,
          airesponse,
        }));
      })
      .catch((err) => {
        console.warn('AI fetch failed after navigation:', err);
      });

    setLoading(false);
  };

  return (
    <>
      {/* Top bar with logo */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        background: '#232830',
        padding: '0.8rem 1.2rem',
        display: 'flex',
        alignItems: 'center',
        zIndex: 1000,
        height: '60px'
      }}>
        <img src="/assets/fintorro-logo.png" alt="FinTorro" style={{ height: '36px' }} />
      </div>

      <div style={{ background: '#232830', color: '#fff', minHeight: 'calc(100vh - 100px)', paddingTop: '80px', fontFamily: 'Nunito Sans, sans-serif' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 1rem' }}>
        <h1 style={{ fontSize: '2.4rem', fontWeight: '800', textAlign: 'center', color: '#ffffff' }}>📈✨ Pass prop trading challenges with AI?</h1>
 <p style={{ fontSize: '1.2rem', textAlign: 'center', marginBottom: '2rem', color: '#ffffff' }}>
 Confused about smart money concepts? Just ask.
</p>

          {/* Textarea + Ask Button */}
          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. What are smart money concepts?"
              rows={5}
              style={{
                width: '100%',
                padding: '1rem',
                borderRadius: '16px',
                fontSize: '1.1rem',
                border: '1px solid #ccc',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'Nunito Sans, sans-serif',
                color: '#fff',
                backgroundColor: '#2C2F36',
              }}
            />
            <button
              onClick={() => handleAskAI()}
              style={{
                position: 'absolute',
                bottom: '12px',
                right: '12px',
                padding: '0.75rem 1.25rem',
                fontWeight: '700',
                fontSize: '1rem',
                backgroundColor: '#FF7C24',
                color: '#fff',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
              }}
            >
              {loading ? 'Thinking...' : 'Ask AI'}
            </button>
          </div>

          {/* Suggestions */}
          <div style={{ display: 'grid', gap: '2rem', marginBottom: '2rem' }}>
            {suggestionGroups.map((group, gIdx) => (
              <div key={gIdx}>
                <h3 style={{ color: '#fff', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {group.title}
                  {/* 👆 Little animated tap icon */}
                  <Lottie animationData={tapAnimation} loop={true} style={{ width: 32, height: 32 }} />
                </h3>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                    gap: '1rem'
                  }}
                >
                  {group.items.map((item, idx) => (
                    <div
                      key={`${gIdx}-${idx}`}
                      onClick={() => {
                        setInput(item.text);
                        trackEvent('expo_suggestion_click', {
                          topic: item.text,
                          user_id: auth.currentUser?.uid || 'anonymous',
                          channel: 'web',
                          timestamp: Date.now(),
                        });
                        // Auto-submit immediately
                        setTimeout(() => {
                          handleAskAI(item.text);
                        }, 0);
                      }}
                      style={{
                        background: lightColors[idx % lightColors.length],
                        color: '#232830',
                        borderRadius: '12px',
                        padding: '0.9rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.4rem',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        fontFamily: 'Nunito Sans, sans-serif',
                        cursor: 'pointer',
                        transition: 'all 0.25s ease-in-out',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                        animation: 'pulse 2s infinite',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.08)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.2)';
                      }}
                    >
                      <div style={{ fontSize: '1.6rem' }}>{item.icon}</div>
                      <div style={{ textAlign: 'center' }}>{item.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PropFundingAILanding;




// import React, { useState, useRef } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { v4 as uuidv4 } from 'uuid';
// import Header from '../components/Header.jsx';
// import Footer from '../components/Footer.jsx';
// import { logEvent } from '../utils/analytics/GoogleAnalytics.js';
// import { askAhaMomentAI } from '../api/askAhaMomentAI.js';
// import { getAuth } from 'firebase/auth';

// import { trackEvent } from '../utils/analytics/trackEvent.js';
// import { useEffect } from 'react';

// import { FaLightbulb, FaProjectDiagram, FaWater, FaUniversity } from 'react-icons/fa';



// const PropFundingAILanding = () => {
//   const [input, setInput] = useState('');
//   const [loading, setLoading] = useState(false);
//   const navigate = useNavigate();
//   const chatEndRef = useRef(null);
//   const auth = getAuth();


  
//   useEffect(() => {
//     trackEvent('aha_page_view', {
//       screen: 'AhaMomentAILanding',
//       channel: 'web',
//       timestamp: Date.now(),
//     });
//   }, []);


//   const lightColors = ["#FFE5D0", "#D6F0FF", "#DFFFE2", "#F0E5FF", "#F9F9F9"];

//   const suggestionGroups = [
//     {
//       title: "📘 Basics",
//       color: "linear-gradient(135deg, #FF7C24, #FF9F5A)",
//       items: [
//         { text: "Smart Money Concepts", icon: <FaLightbulb /> },
//         { text: "Market Structure", icon: <FaProjectDiagram /> },
//         { text: "Directional Bias", icon: <FaLightbulb /> },
//         { text: "Chart Patterns", icon: <FaProjectDiagram /> },
//       ]
//     },
//     {
//       title: "💧 Liquidity",
//       color: "linear-gradient(135deg, #4DB6E2, #6DD5FA)",
//       items: [
//         { text: "Liquidity Grabs", icon: <FaWater /> },
//         { text: "Liquidity Pools", icon: <FaWater /> },
//         { text: "Liquidity Sweeps", icon: <FaWater /> },
//         { text: "Stop Hunt", icon: <FaWater /> },
//       ]
//     },
//     {
//       title: "🏦 Order Blocks",
//       color: "linear-gradient(135deg, #43C6AC, #6FCF97)",
//       items: [
//         { text: "Order Block", icon: <FaUniversity /> },
//         { text: "Breaker Blocks", icon: <FaUniversity /> },
//         { text: "Mitigation Block", icon: <FaUniversity /> },
//         { text: "Confirm Order Block", icon: <FaUniversity /> },
//       ]
//     },
//     {
//       title: "⚡ Advanced",
//       color: "linear-gradient(135deg, #9B51E0, #BB6BD9)",
//       items: [
//         { text: "Fair Value Gap", icon: <FaLightbulb /> },
//         { text: "Displacement", icon: <FaLightbulb /> },
//         { text: "Change of Character", icon: <FaLightbulb /> },
//         { text: "Institutional Order Flow", icon: <FaUniversity /> },
//       ]
//     }
//   ];
  


  

//   const handleAskAI = async (overrideInput) => {
//     const usermessage = (overrideInput || input).trim();
//     if (!usermessage) return;
  
//     setLoading(true);
  
//     const chatSessionId = uuidv4();
//     const user = auth.currentUser;
//     const userId = user?.uid || 'anonymous';
  
//     trackEvent('aha_ask_ai_button_clicked', {
//       screen: 'AhaMomentAILanding',
//       user_id: userId,
//       channel: 'web',
//       message_length: usermessage.length,
//       timestamp: Date.now(),
//     });
  
//     // ✅ GA4 Tracking
//     trackEvent('aha_ask_ai_click', {
//       screen: 'AhaMomentAILanding',
//       user_id: userId,
//       chatsessionid: chatSessionId,
//       message_length: usermessage.length,
//       channel: 'web',
//       timestamp: Date.now(),
//     });
  
//     // ✅ Navigate immediately
//     navigate(`/learn/propfunding/space/${chatSessionId}`, {
//       state: { question: usermessage },
//     });
  
//     // 🔁 Background AI fetch
//     askAhaMomentAI({
//       userId,
//       chatsessionid: chatSessionId,
//       usermessage,
//       messageCount: 0,
//     })
//       .then((airesponse) => {
//         sessionStorage.setItem(`ahaResponse-${chatSessionId}`, JSON.stringify({
//           question: usermessage,
//           airesponse,
//         }));
//       })
//       .catch((err) => {
//         console.warn('AI fetch failed after navigation:', err);
//       });
  
//     setLoading(false);
//   };
  

  

  

//   return (
//     <>


//       {/* <Header /> */}
//       <div style={{
//   position: 'fixed',
//   top: 0,
//   left: 0,
//   width: '100%',
//   background: '#232830',
//   padding: '0.8rem 1.2rem',
//   display: 'flex',
//   alignItems: 'center',
//   zIndex: 1000,
//   height: '60px'
// }}>
//   <img src="/assets/fintorro-logo.png" alt="FinTorro" style={{ height: '36px' }} />
// </div>





//       <div style={{ background: '#232830', color: '#fff', minHeight: 'calc(100vh - 100px)', paddingTop: '80px', fontFamily: 'Nunito Sans, sans-serif' }}>
//         <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 1rem' }}>
//           <h1 style={{ fontSize: '2.4rem', fontWeight: '800', textAlign: 'center', color: '#ffffff' }}>📈✨ Pass prop trading challenges with AI?</h1>
//           <p style={{ fontSize: '1.2rem', textAlign: 'center', marginBottom: '2rem', color: '#ffffff' }}>
//             Confused about smart money concepts? Just ask.
//           </p>

//           <div style={{ position: 'relative', marginBottom: '1rem' }}>
//           <textarea
//   value={input}
//   onChange={(e) => setInput(e.target.value)}
//   placeholder="e.g. What are smart money concepts?"
//   rows={5}
//   style={{
//     width: '100%',
//     padding: '1rem',
//     borderRadius: '16px',
//     fontSize: '1.1rem',
//     border: '1px solid #ccc',
//     outline: 'none',
//     resize: 'vertical',
//     fontFamily: 'Nunito Sans, sans-serif',
//     color: '#fff',
//     backgroundColor: '#2C2F36',
//   }}
// />
//             <button
//               onClick={handleAskAI}
//               style={{
//                 position: 'absolute',
//                 bottom: '12px',
//                 right: '12px',
//                 padding: '0.75rem 1.25rem',
//                 fontWeight: '700',
//                 fontSize: '1rem',
//                 backgroundColor: '#FF7C24',
//                 color: '#fff',
//                 border: 'none',
//                 borderRadius: '10px',
//                 cursor: 'pointer',
//               }}
//             >
//                 {loading ? 'Thinking...' : 'Ask AI'}
//             </button>
//           </div>

//           <div style={{ display: 'grid', gap: '2rem', marginBottom: '2rem' }}>

//   {suggestionGroups.map((group, gIdx) => (
//     <div key={gIdx}>
//       <h3 style={{ color: '#fff', marginBottom: '1rem' }}>{group.title}</h3>
//       <div 
//         style={{ 
//           display: 'grid', 
//           gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', 
//           gap: '1rem' 
//         }}
//       >
//             {group.items.map((item, idx) => (
//           <div
//             key={idx}
//             onClick={() => {
//               setInput(item.text);
            
//               trackEvent('expo_suggestion_click', {
//                 topic: item.text,
//                 user_id: auth.currentUser?.uid || 'anonymous',
//                 channel: 'web',
//                 timestamp: Date.now(),
//               });
            
//               // Wait for state update, then auto-submit
//               setTimeout(() => {
//                 handleAskAI(item.text); // pass the text directly
//               }, 0);
//             }}
//             style={{
//               background: lightColors[idx % lightColors.length],
//               color: '#232830',
//               borderRadius: '12px',
//               padding: '0.9rem',
//               display: 'flex',
//               flexDirection: 'column',
//               alignItems: 'center',
//               justifyContent: 'center',
//               gap: '0.4rem',
//               fontWeight: '600',
//               fontSize: '0.9rem',
//               fontFamily: 'Nunito Sans, sans-serif',
//               cursor: 'pointer',
//               transition: 'all 0.2s ease-in-out',
//               boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
//             }}
            
            
// onMouseEnter={(e) => {
//   e.currentTarget.style.transform = 'scale(1.08)';
//   e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.3)';
// }}
// onMouseLeave={(e) => {
//   e.currentTarget.style.transform = 'scale(1)';
//   e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.2)';
// }}

//           >
//             <div style={{ fontSize: '1.6rem' }}>{item.icon}</div>
//             <div style={{ textAlign: 'center' }}>{item.text}</div>
//           </div>
//         ))}
//       </div>
//     </div>
//   ))}
// </div>


//         </div>
//       </div>
//       <Footer />
//     </>
//   );
// };

// export default PropFundingAILanding;

